import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Send,
  RotateCcw,
  Code2,
  Play,
  Settings,
  Loader2,
  Pencil,
  Save,
  FolderOpen,
  Trash2,
  X,
  Palette,
  Paperclip,
  FileText,
  Image,
  FolderPlus,
  Film,
} from "lucide-react";
import { apiRequest, uploadFile } from "@/lib/queryClient";
import { DynamicOverlayPreview } from "@/lib/remotion-preview-runtime";
import type {
  MgCaptionStyle,
  MgStyleTemplate,
  MgTemplateOverlay,
  MgPresetPack,
} from "@shared/schema";
import { AiGeneratingOverlay } from "@/components/ui/ai-generating-overlay";

// ─── Types ───────────────────────────────────────────────────────

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  editInfo?: { description: string };
  attachments?: AttachedFile[];
}

interface AttachedFile {
  name: string;
  type: "image" | "csv" | "text" | "video";
  dataUrl?: string;
  thumbnailUrl?: string;
  parsedContent?: string;
  durationSec?: number;
  r2Url?: string;
  r2Key?: string;
  file?: File;
}

interface AnimationState {
  code: string;
  compiled: string;
  name: string;
}

type ActiveTab = "code" | "preview";

/**
 * VP-326: odpowiedz `/generate-motion-graphics` i `/follow-up-edit` — ten sam ksztalt.
 * Pola opcjonalne, bo oba endpointy oddaja wynik modelu; przy braku `code`/`compiled`
 * zostawiamy dotychczasowa animacje zamiast czyscic podglad.
 */
type MotionGraphicsResponse = {
  code?: string;
  compiled?: string;
  name?: string;
  description?: string;
  editType?: string;
};
type SidePanel = "none" | "templates" | "style";

// ─── Constants ───────────────────────────────────────────────────

const DEFAULT_FPS = 30;
const DEFAULT_DURATION = 180;
const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;

const DEFAULT_CAPTION_STYLE: MgCaptionStyle = {
  bgColor: "rgba(0,0,0,0.75)",
  textColor: "#ffffff",
  fontFamily: "Inter, sans-serif",
  fontWeight: 800,
  fontSize: "1.1em",
  borderRadius: 8,
  dropShadow: true,
  shadowColor: "rgba(0,0,0,0.9)",
  shadowBlur: 8,
  entranceEffect: "none",
  textTransform: "none",
  letterSpacing: "0",
  padding: "6px 14px",
};

const ENTRANCE_EFFECTS: Array<{ value: MgCaptionStyle["entranceEffect"]; label: string }> = [
  { value: "none", label: "entranceEffect.none" },
  { value: "fade-in", label: "Fade In" },
  { value: "scale-up", label: "Scale Up" },
  { value: "slide-up", label: "Slide Up" },
  { value: "bounce-in", label: "Bounce" },
];

const FONT_OPTIONS = [
  "Inter, sans-serif",
  "Arial, Helvetica, sans-serif",
  "Georgia, serif",
  "monospace",
  "system-ui",
];

const EXAMPLE_PROMPTS = [
  {
    label: "examples.gradientReveal",
    prompt:
      "Fullscreen gradient background reveal. Dark navy-to-purple gradient (#0a0a1a → #2d1b69). A bold white headline slides up from the bottom with a spring bounce. Thin accent line draws from left under the headline. Subtext fades in below. Entrance is a fast wipe from bottom-left. 9:16 vertical format.",
  },
  {
    label: "Typewriter text",
    prompt:
      'Generate the text "Hello world" with "world" in yellow highlighting. Black text, white background. Mimic a typewriter effect with a blinking cursor.',
  },
  {
    label: "Chat bubbles",
    prompt:
      "WhatsApp-style chat bubbles appearing one by one with bouncy spring animation, alternating sent and received.",
  },
  {
    label: "Bar chart",
    prompt:
      "Animated histogram with spring animations showing weekly sales data: Mon 65, Tue 85, Wed 45, Thu 95, Fri 75.",
  },
  {
    label: "Counter",
    prompt:
      "Three animated metric counters: Users (10,000), Revenue ($50,000), Growth (127%) counting up from zero with staggered timing.",
  },
];

const TEMPLATE_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "custom", label: "templateCategories.custom" },
  { value: "minimal", label: "templateCategories.minimal" },
  { value: "bold", label: "templateCategories.bold" },
  { value: "neon", label: "templateCategories.neon" },
  { value: "corporate", label: "templateCategories.corporate" },
  { value: "social", label: "templateCategories.social" },
];

// ─── Main Component ──────────────────────────────────────────────

export default function MotionGraphicsStudio() {
  const { t } = useTranslation("motionGraphicsStudio");
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Animation state
  const [animation, setAnimation] = useState<AnimationState | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>("preview");
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const animFrameRef = useRef<number>(0);

  // Settings
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [durationInFrames, setDurationInFrames] = useState(DEFAULT_DURATION);
  const [showSettings, setShowSettings] = useState(false);

  // Caption style
  const [captionStyle, setCaptionStyle] = useState<MgCaptionStyle>({ ...DEFAULT_CAPTION_STYLE });

  // Templates
  const [templates, setTemplates] = useState<MgStyleTemplate[]>([]);
  const [sidePanel, setSidePanel] = useState<SidePanel>("none");
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templateCategory, setTemplateCategory] = useState("custom");
  const [templatePackId, setTemplatePackId] = useState<string>("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Packs / Folders
  const [packs, setPacks] = useState<MgPresetPack[]>([]);
  const [showNewPackDialog, setShowNewPackDialog] = useState(false);
  const [newPackName, setNewPackName] = useState("");
  const [isCreatingPack, setIsCreatingPack] = useState(false);

  // File attachments for AI context
  const [pendingAttachments, setPendingAttachments] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Error
  const [error, setError] = useState<string | null>(null);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load templates and packs on mount
  useEffect(() => {
    loadTemplates();
    loadPacks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Playback loop
  useEffect(() => {
    if (!isPlaying || !animation) return;

    let lastTime = performance.now();
    const frameDuration = 1000 / fps;

    const tick = (now: number) => {
      const delta = now - lastTime;
      if (delta >= frameDuration) {
        setCurrentFrame((prev) => {
          const next = prev + 1;
          return next >= durationInFrames ? 0 : next;
        });
        lastTime = now - (delta % frameDuration);
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, animation, fps, durationInFrames]);

  // ─── API Calls ───────────────────────────────────────────────

  const loadTemplates = useCallback(async () => {
    try {
      const data = await apiRequest<MgStyleTemplate[]>("GET", "/api/video-editor/style-templates");
      setTemplates(data);
    } catch {
      /* ignore on initial load */
    }
  }, []);

  const loadPacks = useCallback(async () => {
    try {
      const data = await apiRequest<MgPresetPack[]>("GET", "/api/video-editor/preset-packs");
      setPacks(data);
    } catch {
      /* ignore */
    }
  }, []);

  const createPack = useCallback(async () => {
    if (!newPackName.trim()) return;
    setIsCreatingPack(true);
    try {
      const data = await apiRequest<MgPresetPack>("POST", "/api/video-editor/preset-packs", {
        name: newPackName.trim(),
      });
      setPacks((prev) => [...prev, data]);
      setTemplatePackId(data.id);
      setShowNewPackDialog(false);
      setNewPackName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.createFolder"));
    } finally {
      setIsCreatingPack(false);
    }
  }, [newPackName, t]);

  const extractVideoThumbnail = useCallback(
    (file: File): Promise<{ thumbnailUrl: string; durationSec: number }> => {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.src = url;
        video.currentTime = 0.5;
        video.onloadeddata = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 160;
          canvas.height = Math.round(160 * (video.videoHeight / video.videoWidth));
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL("image/jpeg", 0.7);
          const durationSec = video.duration;
          URL.revokeObjectURL(url);
          resolve({ thumbnailUrl, durationSec });
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve({ thumbnailUrl: "", durationSec: 0 });
        };
      });
    },
    []
  );

  const parseAttachment = useCallback(
    async (file: File): Promise<AttachedFile> => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      const isCsv = file.name.endsWith(".csv") || file.type === "text/csv";

      if (isVideo) {
        const { thumbnailUrl, durationSec } = await extractVideoThumbnail(file);
        return { name: file.name, type: "video", thumbnailUrl, durationSec };
      }

      if (isImage) {
        // Read preview as dataUrl, keep file reference for R2 upload
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        return { name: file.name, type: "image", dataUrl, file };
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          if (isCsv) {
            const rows = content
              .split("\n")
              .slice(0, 20)
              .map((r) => r.trim())
              .filter(Boolean);
            resolve({ name: file.name, type: "csv", parsedContent: rows.join("\n") });
          } else {
            resolve({ name: file.name, type: "text", parsedContent: content.slice(0, 2000) });
          }
        };
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },
    [extractVideoThumbnail]
  );

  const uploadAssetToR2 = useCallback(async (att: AttachedFile): Promise<AttachedFile> => {
    if (att.type !== "image" || !att.file) return att;

    try {
      // 1. Get presigned URL
      const { uploadUrl, getUrl, key } = await apiRequest<{
        uploadUrl: string;
        getUrl: string;
        key: string;
      }>("POST", "/api/video-editor/mg-asset-presign", {
        filename: att.file.name,
        mimeType: att.file.type || "image/png",
      });

      // 2. Direct PUT to R2
      try {
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: att.file,
          headers: { "Content-Type": att.file.type || "image/png" },
        });
        if (putRes.ok) return { ...att, r2Url: getUrl, r2Key: key };
      } catch {
        // CORS fallback
      }

      // 3. Fallback: server proxy
      const formData = new FormData();
      formData.append("file", att.file);
      const result = (await uploadFile("/api/video-editor/mg-asset-upload", formData)) as {
        url?: string;
        r2Key?: string;
      };
      return { ...att, r2Url: result.url, r2Key: result.r2Key };
    } catch (err) {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.error("[MG] R2 upload failed, falling back to base64:", err);
      return att;
    }
  }, []);

  const handleFileAttach = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      const parsed = await Promise.all(Array.from(files).map(parseAttachment));
      setPendingAttachments((prev) => [...prev, ...parsed]);
    },
    [parseAttachment]
  );

  const handleGenerate = useCallback(
    async (prompt: string) => {
      if (!prompt.trim() || isGenerating) return;

      setIsGenerating(true);
      setError(null);

      const attachments = [...pendingAttachments];
      const userMsg: ChatMessage = {
        role: "user",
        content: prompt,
        timestamp: new Date(),
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setPendingAttachments([]);

      // Upload images to R2 for direct URL access in Remotion
      const uploadedAttachments = await Promise.all(
        attachments.map((att) =>
          att.type === "image" ? uploadAssetToR2(att) : Promise.resolve(att)
        )
      );

      // Collect asset URLs for images successfully uploaded to R2
      const assetUrls = uploadedAttachments
        .filter((att) => att.type === "image" && att.r2Url)
        .map((att) => ({ name: att.name, url: att.r2Url! }));

      // Build enriched prompt with attachment context
      let enrichedPrompt = prompt;
      for (const att of uploadedAttachments) {
        if (att.type === "csv" && att.parsedContent) {
          enrichedPrompt += `\n\n[Dane CSV z pliku "${att.name}"]:\n${att.parsedContent}`;
        } else if (att.type === "text" && att.parsedContent) {
          enrichedPrompt += `\n\n[Treść pliku "${att.name}"]:\n${att.parsedContent}`;
        } else if (att.type === "image" && att.r2Url) {
          enrichedPrompt += `\n\n[Użytkownik dołączył obraz "${att.name}" — użyj tego obrazu w animacji przez <Img src="..." />]`;
        } else if (att.type === "image") {
          enrichedPrompt += `\n\n[Użytkownik dołączył obraz "${att.name}" jako referencję wizualną — dopasuj styl/kolory animacji do referencji]`;
        } else if (att.type === "video") {
          enrichedPrompt += `\n\n[Użytkownik dołączył wideo "${att.name}" (${att.durationSec ? Math.round(att.durationSec) + "s" : "nieznana długość"}) jako referencję stylu. Zainspiruj się stylem, rytmem i estetyką tego wideo przy tworzeniu animacji]`;
        }
      }

      try {
        if (animation) {
          const result = await apiRequest<MotionGraphicsResponse>(
            "POST",
            "/api/video-editor/follow-up-edit",
            {
              currentCode: animation.code,
              editPrompt: enrichedPrompt,
              assetUrls: assetUrls.length > 0 ? assetUrls : undefined,
            }
          );
          setAnimation({
            code: result.code ?? "",
            compiled: result.compiled ?? "",
            name: result.name ?? "",
          });
          setCurrentFrame(0);
          setIsPlaying(true);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: result.description ?? "",
              timestamp: new Date(),
              editInfo:
                result.editType === "edit" && result.description
                  ? { description: result.description }
                  : undefined,
            },
          ]);
        } else {
          const result = await apiRequest<MotionGraphicsResponse>(
            "POST",
            "/api/video-editor/generate-motion-graphics",
            {
              prompt: enrichedPrompt,
              durationInFrames,
              fps,
              width: DEFAULT_WIDTH,
              height: DEFAULT_HEIGHT,
              assetUrls: assetUrls.length > 0 ? assetUrls : undefined,
            }
          );
          setAnimation({
            code: result.code ?? "",
            compiled: result.compiled ?? "",
            name: result.name ?? "",
          });
          setCurrentFrame(0);
          setIsPlaying(true);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: t("chat.animationGenerated"),
              timestamp: new Date(),
            },
          ]);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Generation failed";
        setError(msg);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `${t("chat.errorPrefix")}${msg}`, timestamp: new Date() },
        ]);
      } finally {
        setIsGenerating(false);
      }
    },
    [animation, isGenerating, durationInFrames, fps, pendingAttachments, uploadAssetToR2, t]
  );

  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim()) return;
    setIsSaving(true);
    try {
      const overlays: MgTemplateOverlay[] = animation
        ? [
            {
              type: "custom-code",
              code: animation.code,
              compiled: animation.compiled,
              name: animation.name,
              triggerMode: "per-subtitle" as const,
              durationFrames: durationInFrames,
            },
          ]
        : [];

      await apiRequest("POST", "/api/video-editor/style-templates", {
        name: templateName.trim(),
        description: templateDesc.trim() || null,
        category: templateCategory,
        packId: templatePackId || null,
        captionStyle,
        overlays,
        previewCode: animation?.code ?? null,
        previewCompiled: animation?.compiled ?? null,
        fps,
      });

      setShowSaveDialog(false);
      setTemplateName("");
      setTemplateDesc("");
      setTemplatePackId("");
      await loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setIsSaving(false);
    }
  }, [
    templateName,
    templateDesc,
    templateCategory,
    captionStyle,
    animation,
    durationInFrames,
    fps,
    loadTemplates,
    templatePackId,
  ]);

  const handleDeleteTemplate = useCallback(
    async (id: string) => {
      try {
        await apiRequest("DELETE", `/api/video-editor/style-templates/${id}`);
        await loadTemplates();
      } catch {
        /* ignore */
      }
    },
    [loadTemplates]
  );

  const handleLoadTemplate = useCallback((template: MgStyleTemplate) => {
    setCaptionStyle(template.captionStyle);
    if (template.previewCode && template.previewCompiled) {
      setAnimation({
        code: template.previewCode,
        compiled: template.previewCompiled,
        name: template.name,
      });
      setCurrentFrame(0);
      setIsPlaying(true);
    }
    if (template.fps) setFps(template.fps);
    setSidePanel("none");
  }, []);

  const handleReset = useCallback(() => {
    setMessages([]);
    setAnimation(null);
    setError(null);
    setCurrentFrame(0);
    setIsPlaying(false);
    setInputValue("");
    setCaptionStyle({ ...DEFAULT_CAPTION_STYLE });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text =
      inputValue.trim() ||
      (pendingAttachments.length > 0
        ? `[${pendingAttachments.map((a) => a.name).join(", ")}]`
        : "");
    if (text) handleGenerate(text);
  };

  const updateCaptionField = <K extends keyof MgCaptionStyle>(key: K, value: MgCaptionStyle[K]) => {
    setCaptionStyle((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div className="flex h-full bg-[#0a0a0f] text-white overflow-hidden relative">
      {/* ─── Left: Chat Panel ─── */}
      <div className="w-[380px] flex flex-col border-r border-white/[0.08] shrink-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
          <span className="text-sm font-medium text-white/70">{t("header")}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSidePanel(sidePanel === "templates" ? "none" : "templates")}
              className={`p-1.5 rounded-md transition ${sidePanel === "templates" ? "bg-blue-500/20 text-blue-400" : "text-white/30 hover:text-white/60 hover:bg-white/[0.06]"}`}
              title={t("tooltips.templates")}
            >
              <FolderOpen size={13} />
            </button>
            <button
              onClick={() => setSidePanel(sidePanel === "style" ? "none" : "style")}
              className={`p-1.5 rounded-md transition ${sidePanel === "style" ? "bg-violet-500/20 text-violet-400" : "text-white/30 hover:text-white/60 hover:bg-white/[0.06]"}`}
              title={t("tooltips.captionStyle")}
            >
              <Palette size={13} />
            </button>
            <button
              onClick={handleReset}
              className="p-1.5 rounded-md text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition"
              title={t("tooltips.reset")}
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {messages.length === 0 && !isGenerating && sidePanel === "none" && (
            <div className="space-y-3 mt-4">
              <p className="text-xs text-white/30 uppercase tracking-wider">
                {t("examples.title")}
              </p>
              {EXAMPLE_PROMPTS.map((ex) => (
                <button
                  key={ex.label}
                  onClick={() => handleGenerate(ex.prompt)}
                  className="w-full text-left px-3 py-2.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition text-sm text-white/60 hover:text-white/80"
                >
                  {ex.label.startsWith("examples.") ? t(ex.label) : ex.label}
                </button>
              ))}
            </div>
          )}

          {messages.length === 0 && isGenerating && (
            <div className="mt-8">
              <AiGeneratingOverlay showProgress />
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold ${msg.role === "assistant" ? "text-blue-400" : "text-white/50"}`}
                >
                  {msg.role === "assistant" ? "Assistant" : "You"}
                </span>
                <span className="text-[10px] text-white/20">
                  {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {msg.role === "user" ? (
                <div className="bg-white/[0.06] rounded-lg px-3 py-2 text-sm text-white/80 space-y-1">
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {msg.attachments.map((att, j) => (
                        <div
                          key={j}
                          className="flex items-center gap-1 rounded-lg bg-white/[0.08] text-[9px] text-white/40 overflow-hidden"
                        >
                          {att.type === "video" && att.thumbnailUrl ? (
                            <img
                              src={att.thumbnailUrl}
                              alt={att.name}
                              className="h-7 w-10 object-cover"
                            />
                          ) : att.type === "image" && att.dataUrl ? (
                            <img
                              src={att.dataUrl}
                              alt={att.name}
                              className="h-7 w-7 object-cover"
                            />
                          ) : null}
                          <span className="px-1.5 py-0.5 flex items-center gap-1">
                            {att.type === "video" ? (
                              <Film size={8} className="text-blue-400" />
                            ) : att.type === "image" ? (
                              <Image size={8} />
                            ) : (
                              <FileText size={8} />
                            )}
                            {att.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.content}
                </div>
              ) : (
                <div className="text-sm text-white/60">
                  {msg.content}
                  {msg.editInfo && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-white/30">
                      <Pencil size={10} />
                      {msg.editInfo.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {isGenerating && <AiGeneratingOverlay compact message={t("chat.generatingMessage")} />}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/[0.08]">
          {/* Pending attachments */}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {pendingAttachments.map((att, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 rounded-lg bg-white/[0.08] border border-white/[0.1] text-[10px] text-white/60 overflow-hidden"
                >
                  {att.type === "video" && att.thumbnailUrl ? (
                    <img src={att.thumbnailUrl} alt={att.name} className="h-9 w-14 object-cover" />
                  ) : att.type === "image" && att.dataUrl ? (
                    <img src={att.dataUrl} alt={att.name} className="h-9 w-9 object-cover" />
                  ) : null}
                  <div className="flex items-center gap-1 px-2 py-0.5">
                    {att.type === "video" ? (
                      <Film size={9} className="text-blue-400" />
                    ) : att.type === "image" ? (
                      <Image size={9} />
                    ) : (
                      <FileText size={9} />
                    )}
                    <span className="max-w-[80px] truncate">{att.name}</span>
                    {att.type === "video" && att.durationSec ? (
                      <span className="text-white/30">{Math.round(att.durationSec)}s</span>
                    ) : null}
                  </div>
                  <button
                    onClick={() => setPendingAttachments((prev) => prev.filter((_, j) => j !== i))}
                    className="pr-1.5 text-white/30 hover:text-white/60"
                  >
                    <X size={8} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg border border-white/[0.08] px-3 py-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-1 rounded text-white/30 hover:text-white/60 transition shrink-0"
                title={t("tooltips.attachFile")}
              >
                <Paperclip size={13} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm,.csv,.txt,.json"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleFileAttach(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={animation ? t("input.editPlaceholder") : t("input.descPlaceholder")}
                disabled={isGenerating}
                className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={(!inputValue.trim() && pendingAttachments.length === 0) || isGenerating}
                className="p-1.5 rounded-full bg-blue-500 hover:bg-blue-600 disabled:opacity-30 disabled:hover:bg-blue-500 transition"
              >
                <Send size={12} className="text-white" />
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ─── Side Panel: Templates / Style ─── */}
      {sidePanel !== "none" && (
        <div className="w-[300px] flex flex-col border-r border-white/[0.08] bg-[#0d0d14] shrink-0">
          {sidePanel === "templates" && (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
                <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                  {t("templatesPanel.title")}
                </span>
                <button
                  onClick={() => setSidePanel("none")}
                  className="p-1 text-white/20 hover:text-white/50"
                >
                  <X size={12} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {/* Save new button */}
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-white/[0.12] text-white/40 hover:text-white/70 hover:border-white/[0.2] hover:bg-white/[0.03] transition text-xs"
                >
                  <Save size={12} />
                  {t("templatesPanel.saveCurrentAsTemplate")}
                </button>

                {templates.length === 0 && (
                  <p className="text-xs text-white/20 text-center py-4">
                    {t("templatesPanel.noTemplates")}
                    <br />
                    {t("templatesPanel.noTemplatesHint")}
                  </p>
                )}

                {/* Templates grouped by pack/folder */}
                {packs.length > 0 &&
                  packs.map((pack) => {
                    const packTemplates = templates.filter((t) => t.packId === pack.id);
                    if (packTemplates.length === 0) return null;
                    return (
                      <div key={pack.id} className="space-y-1">
                        <div className="flex items-center gap-1.5 px-1 py-0.5">
                          <FolderOpen size={10} className="text-violet-400/60" />
                          <span className="text-[10px] font-semibold text-violet-400/60 uppercase tracking-wider truncate">
                            {pack.name}
                          </span>
                          <span className="text-[9px] text-white/20 ml-auto shrink-0">
                            {packTemplates.length} {t("templatesPanel.countSuffix")}
                          </span>
                        </div>
                        {packTemplates.map((tmpl) => (
                          <div
                            key={tmpl.id}
                            className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-violet-500/[0.06] hover:border-violet-500/20 transition group cursor-pointer ml-2"
                            onClick={() => handleLoadTemplate(tmpl)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-white/80 truncate">
                                {tmpl.name}
                              </div>
                              {tmpl.description && (
                                <div className="text-[10px] text-white/30 truncate mt-0.5">
                                  {tmpl.description}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTemplate(tmpl.id);
                              }}
                              className="p-1 text-white/10 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                {/* Templates without a pack */}
                {(() => {
                  const unfoldered = templates.filter((tmpl) => !tmpl.packId);
                  if (unfoldered.length === 0) return null;
                  return (
                    <div className="space-y-1">
                      {packs.length > 0 && (
                        <div className="flex items-center gap-1.5 px-1 py-0.5">
                          <span className="text-[10px] font-semibold text-white/25 uppercase tracking-wider">
                            {t("templatesPanel.noFolder")}
                          </span>
                        </div>
                      )}
                      {unfoldered.map((tmpl) => (
                        <div
                          key={tmpl.id}
                          className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition group cursor-pointer"
                          onClick={() => handleLoadTemplate(tmpl)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-white/80 truncate">
                              {tmpl.name}
                            </div>
                            {tmpl.description && (
                              <div className="text-[10px] text-white/30 truncate mt-0.5">
                                {tmpl.description}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] text-white/20 bg-white/[0.06] px-1.5 py-0.5 rounded">
                                {tmpl.category}
                              </span>
                              {tmpl.overlays.length > 0 && (
                                <span className="text-[9px] text-violet-400/50">
                                  {tmpl.overlays.length} overlay
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(tmpl.id);
                            }}
                            className="p-1 text-white/10 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {sidePanel === "style" && (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
                <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                  {t("stylePanel.title")}
                </span>
                <button
                  onClick={() => setSidePanel("none")}
                  className="p-1 text-white/20 hover:text-white/50"
                >
                  <X size={12} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {/* Preview */}
                <div className="rounded-lg bg-black/60 p-4 flex items-center justify-center">
                  <div
                    style={{
                      backgroundColor: captionStyle.bgColor,
                      color: captionStyle.textColor,
                      fontFamily: captionStyle.fontFamily,
                      fontWeight: captionStyle.fontWeight,
                      fontSize: captionStyle.fontSize,
                      borderRadius: captionStyle.borderRadius,
                      padding: captionStyle.padding,
                      textShadow: captionStyle.dropShadow
                        ? `0 2px ${captionStyle.shadowBlur}px ${captionStyle.shadowColor}`
                        : "none",
                      textTransform:
                        captionStyle.textTransform as React.CSSProperties["textTransform"],
                      letterSpacing: captionStyle.letterSpacing,
                    }}
                  >
                    {t("stylePanel.sampleText")}
                  </div>
                </div>

                {/* Bg color */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.bgColor")}
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={
                        captionStyle.bgColor.startsWith("rgba") ? "#000000" : captionStyle.bgColor
                      }
                      onChange={(e) => updateCaptionField("bgColor", e.target.value)}
                      className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                    />
                    <input
                      type="text"
                      value={captionStyle.bgColor}
                      onChange={(e) => updateCaptionField("bgColor", e.target.value)}
                      className="flex-1 px-2 py-1 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                    />
                  </div>
                </label>

                {/* Text color */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.textColor")}
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={captionStyle.textColor}
                      onChange={(e) => updateCaptionField("textColor", e.target.value)}
                      className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                    />
                    <input
                      type="text"
                      value={captionStyle.textColor}
                      onChange={(e) => updateCaptionField("textColor", e.target.value)}
                      className="flex-1 px-2 py-1 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                    />
                  </div>
                </label>

                {/* Font */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.font")}
                  </span>
                  <select
                    value={captionStyle.fontFamily}
                    onChange={(e) => updateCaptionField("fontFamily", e.target.value)}
                    className="w-full mt-1 px-2 py-1.5 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                  >
                    {FONT_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {f.split(",")[0]}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Font weight */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.fontWeight", { weight: captionStyle.fontWeight })}
                  </span>
                  <input
                    type="range"
                    min={300}
                    max={900}
                    step={100}
                    value={captionStyle.fontWeight}
                    onChange={(e) => updateCaptionField("fontWeight", Number(e.target.value))}
                    className="w-full mt-1"
                  />
                </label>

                {/* Font size */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.fontSize")}
                  </span>
                  <select
                    value={captionStyle.fontSize}
                    onChange={(e) => updateCaptionField("fontSize", e.target.value)}
                    className="w-full mt-1 px-2 py-1.5 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                  >
                    {["0.8em", "0.9em", "1em", "1.1em", "1.2em", "1.3em", "1.5em", "1.8em"].map(
                      (s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      )
                    )}
                  </select>
                </label>

                {/* Border radius */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.borderRadius", { radius: captionStyle.borderRadius })}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={24}
                    value={captionStyle.borderRadius}
                    onChange={(e) => updateCaptionField("borderRadius", Number(e.target.value))}
                    className="w-full mt-1"
                  />
                </label>

                {/* Drop shadow */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={captionStyle.dropShadow}
                    onChange={(e) => updateCaptionField("dropShadow", e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-[10px] text-white/50">Drop Shadow</span>
                </label>
                {captionStyle.dropShadow && (
                  <div className="ml-4 space-y-2">
                    <label className="block">
                      <span className="text-[9px] text-white/30">
                        {t("stylePanel.shadowBlur", { blur: captionStyle.shadowBlur })}
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={24}
                        value={captionStyle.shadowBlur}
                        onChange={(e) => updateCaptionField("shadowBlur", Number(e.target.value))}
                        className="w-full"
                      />
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-white/30">
                        {t("stylePanel.shadowColor")}
                      </span>
                      <input
                        type="text"
                        value={captionStyle.shadowColor}
                        onChange={(e) => updateCaptionField("shadowColor", e.target.value)}
                        className="flex-1 px-2 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
                      />
                    </div>
                  </div>
                )}

                {/* Text transform */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.textTransform")}
                  </span>
                  <select
                    value={captionStyle.textTransform}
                    onChange={(e) =>
                      updateCaptionField(
                        "textTransform",
                        e.target.value as MgCaptionStyle["textTransform"]
                      )
                    }
                    className="w-full mt-1 px-2 py-1.5 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                  >
                    <option value="none">{t("textTransform.none")}</option>
                    <option value="uppercase">{t("textTransform.uppercase")}</option>
                    <option value="lowercase">{t("textTransform.lowercase")}</option>
                  </select>
                </label>

                {/* Entrance effect */}
                <label className="block">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">
                    {t("stylePanel.entranceEffect")}
                  </span>
                  <select
                    value={captionStyle.entranceEffect}
                    onChange={(e) =>
                      updateCaptionField(
                        "entranceEffect",
                        e.target.value as MgCaptionStyle["entranceEffect"]
                      )
                    }
                    className="w-full mt-1 px-2 py-1.5 text-[10px] bg-white/[0.06] border border-white/[0.1] rounded text-white/70"
                  >
                    {ENTRANCE_EFFECTS.map((ef) => (
                      <option key={ef.value} value={ef.value}>
                        {ef.label.startsWith("entranceEffect.") ? t(ef.label) : ef.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── Right: Code + Preview ─── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Tabs + save */}
        <div className="flex items-center gap-1 px-4 pt-3 border-b border-white/[0.08]">
          <button
            onClick={() => setActiveTab("code")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition ${
              activeTab === "code"
                ? "border-blue-500 text-white bg-white/[0.04]"
                : "border-transparent text-white/40 hover:text-white/60"
            }`}
          >
            <Code2 size={13} />
            Remotion Code
          </button>
          <button
            onClick={() => setActiveTab("preview")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition ${
              activeTab === "preview"
                ? "border-blue-500 text-white bg-white/[0.04]"
                : "border-transparent text-white/40 hover:text-white/60"
            }`}
          >
            <Play size={13} />
            Video Preview
          </button>
          <div className="flex-1" />
          {/* Save template button */}
          <button
            onClick={() => {
              setSidePanel("templates");
              setShowSaveDialog(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 transition mb-1"
          >
            <Save size={11} />
            {t("saveTemplateButton")}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          {activeTab === "code" ? (
            <div className="flex-1 overflow-auto rounded-lg bg-[#1e1e2e] border border-white/[0.08]">
              {animation ? (
                <pre className="p-4 text-sm font-mono text-white/80 leading-relaxed whitespace-pre-wrap">
                  {animation.code}
                </pre>
              ) : (
                <div className="flex items-center justify-center h-full text-white/20 text-sm">
                  {t("codeTab.empty")}
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              <div
                className="flex-1 rounded-lg overflow-hidden bg-black relative"
                style={{ maxHeight: "calc(100% - 60px)" }}
              >
                {animation ? (
                  <div className="w-full h-full" style={{ aspectRatio: "16/9" }}>
                    <div className="relative w-full h-full">
                      <DynamicOverlayPreview
                        compiled={animation.compiled}
                        currentFrame={currentFrame}
                        fps={fps}
                        width={DEFAULT_WIDTH}
                        height={DEFAULT_HEIGHT}
                        durationInFrames={durationInFrames}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-white/20 text-sm">
                    {t("previewTab.empty")}
                  </div>
                )}
              </div>

              {/* Playback Controls */}
              {animation && (
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="px-3 py-1.5 rounded-md bg-white/[0.06] hover:bg-white/[0.1] text-xs text-white/70 transition"
                    >
                      {isPlaying ? t("playback.pause") : t("playback.play")}
                    </button>
                    <span className="text-[10px] text-white/30 font-mono">
                      {currentFrame} / {durationInFrames}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-xs text-white/40 transition"
                  >
                    <Settings size={11} />
                    {t("settings.title")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Settings popover */}
        {showSettings && (
          <div className="absolute bottom-20 right-8 bg-[#1a1a2e] border border-white/[0.1] rounded-lg p-4 shadow-2xl z-50 space-y-3 w-64">
            <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">
              {t("settings.title")}
            </h3>
            <label className="flex items-center justify-between text-xs text-white/50">
              {t("settings.duration")}
              <input
                type="number"
                value={durationInFrames}
                onChange={(e) => setDurationInFrames(Number(e.target.value))}
                min={30}
                max={900}
                className="w-20 bg-white/[0.06] border border-white/[0.1] rounded px-2 py-1 text-white text-right text-xs"
              />
            </label>
            <label className="flex items-center justify-between text-xs text-white/50">
              {t("settings.fps")}
              <input
                type="number"
                value={fps}
                onChange={(e) => setFps(Number(e.target.value))}
                min={15}
                max={60}
                className="w-20 bg-white/[0.06] border border-white/[0.1] rounded px-2 py-1 text-white text-right text-xs"
              />
            </label>
            <button
              onClick={() => setShowSettings(false)}
              className="w-full text-center text-xs text-white/40 hover:text-white/60 mt-1"
            >
              {t("settings.close")}
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* ─── Save Template Dialog ─── */}
      {showSaveDialog && (
        <div
          className="absolute inset-0 bg-black/60 z-50 flex items-center justify-center"
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="bg-[#14141e] border border-white/[0.1] rounded-xl p-5 w-[420px] space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white/80">{t("saveDialog.title")}</h3>
            <p className="text-[11px] text-white/30">{t("saveDialog.description")}</p>
            <div className="space-y-3">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t("saveDialog.namePlaceholder")}
                className="w-full px-3 py-2 text-sm bg-white/[0.06] border border-white/[0.1] rounded-lg text-white placeholder-white/20 focus:border-violet-500/50 outline-none"
                autoFocus
              />
              <input
                type="text"
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder={t("saveDialog.descPlaceholder")}
                className="w-full px-3 py-2 text-sm bg-white/[0.06] border border-white/[0.1] rounded-lg text-white placeholder-white/20 focus:border-violet-500/50 outline-none"
              />
              <select
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white/[0.06] border border-white/[0.1] rounded-lg text-white/70"
              >
                {TEMPLATE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(c.label)}
                  </option>
                ))}
              </select>

              {/* Folder / Pack selector */}
              <div className="space-y-1">
                <span className="text-[10px] text-white/40 uppercase tracking-wider">
                  {t("saveDialog.folderLabel")}
                </span>
                <div className="flex items-center gap-2">
                  <select
                    value={templatePackId}
                    onChange={(e) => setTemplatePackId(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm bg-white/[0.06] border border-white/[0.1] rounded-lg text-white/70"
                  >
                    <option value="">{t("saveDialog.noFolder")}</option>
                    {packs.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewPackDialog(true)}
                    className="flex items-center gap-1 px-2.5 py-2 text-[11px] rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/40 hover:text-white/70 transition shrink-0"
                    title={t("saveDialog.newFolderTooltip")}
                  >
                    <FolderPlus size={12} />
                    {t("saveDialog.newFolderButton")}
                  </button>
                </div>
                {templatePackId && (
                  <p className="text-[10px] text-violet-400/60">{t("saveDialog.folderHint")}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-4 py-2 text-xs text-white/40 hover:text-white/60 transition"
              >
                {t("saveDialog.cancel")}
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim() || isSaving}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-violet-500 hover:bg-violet-600 text-white transition disabled:opacity-40"
              >
                {isSaving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                {t("saveDialog.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── New Pack / Folder Dialog ─── */}
      {showNewPackDialog && (
        <div
          className="absolute inset-0 bg-black/60 z-[60] flex items-center justify-center"
          onClick={() => setShowNewPackDialog(false)}
        >
          <div
            className="bg-[#14141e] border border-white/[0.1] rounded-xl p-5 w-[340px] space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white/80">{t("newFolderDialog.title")}</h3>
            <p className="text-[11px] text-white/30">{t("newFolderDialog.description")}</p>
            <input
              type="text"
              value={newPackName}
              onChange={(e) => setNewPackName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newPackName.trim()) createPack();
              }}
              placeholder={t("newFolderDialog.placeholder")}
              className="w-full px-3 py-2 text-sm bg-white/[0.06] border border-white/[0.1] rounded-lg text-white placeholder-white/20 focus:border-violet-500/50 outline-none"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNewPackDialog(false)}
                className="px-4 py-2 text-xs text-white/40 hover:text-white/60 transition"
              >
                {t("newFolderDialog.cancel")}
              </button>
              <button
                onClick={createPack}
                disabled={!newPackName.trim() || isCreatingPack}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-violet-500 hover:bg-violet-600 text-white transition disabled:opacity-40"
              >
                {isCreatingPack ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <FolderPlus size={11} />
                )}
                {t("newFolderDialog.create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
